/* eslint-disable camelcase */

import { buyerNickMask } from 'services/channels/demo';

export interface BasicContent
  extends Partial<
    Pick<
      Conversation.Message,
      | 'textContent'
      | 'videoContent'
      | 'itemContent'
      | 'imageContent'
      | 'stickerContent'
      | 'templateContent'
    >
  > {
  storeId: number;
  conversationId: number;
  fromId: number;
  fromUserName: string;
  toId: number;
  toUserName: string;
  msgFromType: Conversation.MessageFromType;
  msgType: Conversation.MessageType;
  replyId?: string;
  tunnelAccountId: string;
}
export interface TextContent extends Partial<BasicContent> {
  textContent: Conversation.MsgText;
  voucher?: boolean;
  msgType: 'TEXT';
}
export interface ImageContent extends Partial<BasicContent> {
  imageContent: Conversation.MsgImage;
  voucher?: boolean;
  msgType: 'IMAGE';
  localUpload?: boolean;
  extra: string;
}
export interface StickerContent extends Partial<BasicContent> {
  stickerContent: Conversation.MsgSticker;
  msgType: 'STICKER';
}
export interface ItemContent extends Partial<BasicContent> {
  itemContent: Conversation.MsgItem;
  msgType: 'ITEM';
}

export interface OrderContent extends Partial<BasicContent> {
  orderContent: Conversation.MsgOrder;
  msgType: 'ORDER';
}

export interface TextVideoContent extends Partial<BasicContent> {
  textVideoContent: Conversation.MsgTextVideo;
  msgType: 'TEXT_VIDEO';
}

export interface VideoContent extends Partial<BasicContent> {
  videoContent: Conversation.MsgVideo;
  sourceContent?: VideoContentExtra;
  msgType: 'VIDEO';
}

export interface TemplateContent extends Partial<BasicContent> {
  templateContent: Conversation.MsgTemplate;
  msgType: 'TEMPLATE';
}

export interface FollowerContent {
  msgType: 'FOLLOWER';
}

export interface VoucherContent {
  msgType: 'VOUCHER';
  voucherContent: Conversation.MsgVoucher;
}

export interface AttachmentContent extends Partial<BasicContent> {
  msgType: 'ATTACHMENT';
  attachmentContent: Conversation.MsgAttachment;

  // 本地状态：输入框发出的消息才会有这个字段
  autoTranslation?: Omit<Conversation.Translation, 'text'> & {
    text?: string;
    targetLanguage: General.Language;
  };
}

export interface ReturnRefundContent {
  msgType: 'RETURN_REFUND_CARD';
  returnRefundCardContent: Conversation.MsgReturnRefundCard;
}

export interface TtsLogisticsCardContent extends Partial<BasicContent> {
  ttsLogisticsCardContent: Conversation.TtsLogisticsCardContent;
  msgType: 'ORDER';
}

export interface ContnentExtra {
  // no common case yet.
}
export interface VoucherContnentExtra extends ContnentExtra {
  voucher: boolean;
}
export interface ImageContnentExtra extends Partial<VoucherContnentExtra> {
  replyId?: string;
  localUpload?: boolean;
  followerMsg?: boolean;
}
export interface TextContentExtra extends Partial<VoucherContnentExtra> {
  autoTranslation?: Conversation.MsgText['autoTranslation'];
  replyId?: string;
}
export interface VideoContentExtra {
  orderSn?: string;
}
function contentGenerator(type: 'TEXT', data: string, extra?: TextContentExtra): TextContent;
function contentGenerator(
  type: 'IMAGE',
  data: ImageContent['imageContent'],
  extra?: ImageContnentExtra,
): ImageContent;
function contentGenerator(type: 'ITEM', data: ItemContent['itemContent']): ItemContent;
function contentGenerator(type: 'STICKER', data: StickerContent['stickerContent']): StickerContent;
function contentGenerator(type: 'ORDER', data: OrderContent['orderContent']): OrderContent;
function contentGenerator(type: 'FOLLOWER'): FollowerContent;
function contentGenerator(type: 'VOUCHER', data: VoucherContent['voucherContent']): VoucherContent;
function contentGenerator(
  type: 'TEXT_VIDEO',
  data: TextVideoContent['textVideoContent'],
): TextVideoContent;
function contentGenerator(
  type: 'VIDEO',
  data: VideoContent['videoContent'],
  extra?: VideoContentExtra,
): VideoContent;
function contentGenerator(
  type: 'TEMPLATE',
  data: TemplateContent['templateContent'],
): TemplateContent;
function contentGenerator(
  type: 'ATTACHMENT',
  data: AttachmentContent['attachmentContent'],
): AttachmentContent;
function contentGenerator(
  type: 'RETURN_REFUND_CARD',
  data: ReturnRefundContent['returnRefundCardContent'],
): ReturnRefundContent;
function contentGenerator(type: 'FACEBOOK_MESSAGE', data: any): any;
function contentGenerator(type: 'TTS_LOGISTICS_CARD', data: any): any;

function contentGenerator(type: any, data?: any, extra?: any) {
  switch (type) {
    case 'TEXT': {
      return {
        msgType: type,
        voucher: extra?.voucher,
        textContent: {
          text: data,
          originalText: extra?.autoTranslation ? data : '',
          autoTranslation: extra?.autoTranslation,
        },
        replyId: extra?.replyId,
      } as TextContent;
    }
    case 'IMAGE': {
      return {
        msgType: type,
        voucher: extra?.voucher,
        imageContent: data,
        replyId: extra?.replyId,
        localUpload: extra?.localUpload,
        extra: JSON.stringify({
          followerMsg: extra?.followerMsg || false,
        }),
      } as ImageContent;
    }
    case 'ITEM': {
      return {
        msgType: type,
        itemContent: data,
      } as ItemContent;
    }
    case 'STICKER': {
      return {
        msgType: type,
        stickerContent: data,
      } as StickerContent;
    }
    case 'ORDER': {
      return {
        msgType: type,
        orderContent: data,
      } as OrderContent;
    }
    case 'FOLLOWER': {
      return {
        msgType: type,
      } as FollowerContent;
    }
    case 'VOUCHER': {
      return {
        msgType: type,
        voucherContent: data,
      } as VoucherContent;
    }
    case 'TEXT_VIDEO': {
      return {
        msgType: type,
        textVideoContent: data,
      } as TextVideoContent;
    }
    case 'VIDEO': {
      return {
        msgType: type,
        videoContent: data,
        sourceContent: {
          orderSn: extra?.orderSn,
        },
      } as VideoContent;
    }
    case 'TEMPLATE': {
      return {
        msgType: type,
        templateContent: data,
      };
    }
    case 'ATTACHMENT': {
      return {
        msgType: type,
        attachmentContent: data,
      } as AttachmentContent;
    }
    case 'RETURN_REFUND_CARD': {
      return {
        msgType: type,
        returnRefundCardContent: data,
      } as ReturnRefundContent;
    }
    case 'FACEBOOK_MESSAGE': {
      return {
        msgType: type,
        fbMsgContent: data,
      };
    }
    case 'TTS_LOGISTICS_CARD': {
      return {
        msgType: type,
        ttsLogisticsCardContent: data,
      };
    }
  }
  return null;
}
export const genBasicContentFrom = (
  conversation: Conversation.Conversation,
  assistantId: number,
  assistanNick: string,
): BasicContent => {
  const { storeId, conversationId, buyerId, buyerNick, tunnelAccountId } = conversation;
  return {
    storeId: parseInt(storeId, 10),
    conversationId: parseInt(conversationId, 10),
    toId: parseInt(buyerId, 10),
    fromId: assistantId,
    fromUserName: assistanNick,
    toUserName: buyerNickMask(buyerNick),
    msgFromType: 'ASSISTANT',
    msgType: 'TEXT',
    tunnelAccountId,
  };
};
export const genCamlBasicContentFrom = (
  conversation: Conversation.Conversation,
  assistantId: number,
  assistanNick: string,
): Conversation.Message => {
  const { storeId, conversationId, buyerId, buyerNick, region, tunnelAccountId } = conversation;
  return {
    storeId,
    conversationId,
    toId: buyerId,
    toUserName: buyerNickMask(buyerNick),
    fromId: String(assistantId),
    fromUserName: assistanNick,
    msgFromType: 'ASSISTANT',
    messageType: 'TEXT',
    region,
    status: 'PENDING',
    channel: conversation.channel,
    externalId: '',
    yunXinMsgId: '',
    msgId: '',
    sendTime: 0,
    extra: '',
    msgResource: 'CHAT',
    tunnelAccountId,
  };
};
export default contentGenerator;
